import React, { useContext, useState } from 'react';
import { cloneDeep } from 'es-toolkit/object';

import { toastify, useTranslation } from '@testsigma/atoms';

import {
  SalesforceConnection,
  SalesforceConnectionEdition,
} from '@modules/salesforce/models';

import { SalesforceConnectionsService } from '@modules/salesforce/services';

import { getConnectionEnv } from '@modules/salesforce/utils/utils';

import {
  MetadataConnectionModalScreens,
  SelectedSalesForceConnection,
  useSFConnectionState,
} from '@contexts/sf-connection-context';
import { useOAuthFlow } from '@modules/salesforce/shared/hooks/use-oauth-flow';
import { UserPreferenceContext } from '@contexts/user-preference-context';

interface UseEditConnectionProps {
  isSaveOnlyMode: boolean;
  isGlobalConnection?: boolean;
  isTestConnection?: boolean;
}

export const useEditConnection = (props: UseEditConnectionProps) => {
  const { isSaveOnlyMode, isGlobalConnection, isTestConnection } = props;
  const { userPreference } = useContext(UserPreferenceContext);
  const [testIsConnecting, setTestIsConnecting] = useState<boolean>();

  const {
    handleUpdateOverlayMode,
    handleUpdateMetadataConnectionModalScreen,
    selectedConnection,
    handleUpdateSelectedConnection,
    handleReload,
    handleConnectionError,
  } = useSFConnectionState();

  const { t } = useTranslation();

  const projectPreference = userPreference?.preference?.projectPreference;

  const [isConnecting, setIsConnecting] = useState<boolean>();

  const handleClose = (event: React.SyntheticEvent) => {
    event.preventDefault();

    handleUpdateSelectedConnection(null);
    handleUpdateOverlayMode('none');

    if (selectedConnection?.reload) {
      handleReload();
    }
  };

  const handleTestSuccess = () => {
    const screenName = MetadataConnectionModalScreens.METADATA_TEST_SUCCESSFUL;
    handleUpdateMetadataConnectionModalScreen(screenName);
  };

  const handleTestFailure = (error?: string) => {
    const screenName =
      MetadataConnectionModalScreens.CONNECTION_AUTH_FAILURE_SCREEN;
    handleUpdateMetadataConnectionModalScreen(screenName);
    handleConnectionError(error);
  };

  const handleSuccess = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(false);

    const response: SelectedSalesForceConnection = cloneDeep(sfConnectionData);

    response.salesforceConnectionEdition = getConnectionEnv(sfConnectionData);
    response.reload = true;
    response.hasFailed = false;
    handleUpdateSelectedConnection(response);

    if (isSaveOnlyMode) {
      toastify(t('message.connection.update.success'), {
        type: 'success',
        position: 'top-center',
      });
      return;
    }

    handleUpdateMetadataConnectionModalScreen(
      MetadataConnectionModalScreens.CONNECTION_UPDATE_SUCCESS_SCREEN,
    );
  };

  const handleFailure = (
    sfConnectionData: SalesforceConnection,
    error: string,
  ) => {
    setIsConnecting(false);

    const response: SelectedSalesForceConnection = cloneDeep(sfConnectionData);
    response.reload = false;
    response.hasFailed = true;
    handleUpdateSelectedConnection(response);
    handleConnectionError(error);

    if (isSaveOnlyMode) {
      toastify(t('message.connection.update.failure'), {
        type: 'error',
        position: 'top-center',
      });
      return;
    }

    handleUpdateMetadataConnectionModalScreen(
      MetadataConnectionModalScreens.CONNECTION_AUTH_FAILURE_SCREEN,
    );
  };

  const handleTestConnection = (sfConnectionData: SalesforceConnection) => {
    setTestIsConnecting(true);

    const data = cloneDeep(sfConnectionData);
    data.performValidation = !isSaveOnlyMode;
    data.isSandbox =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SANDBOX;
    data.isScratchOrg =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SCRATCH_ORG;
    data.applicationId = isGlobalConnection
      ? selectedApplication?.id
      : projectPreference?.selectedApplicationId;
    handleUpdateSelectedConnection(data);
    SalesforceConnectionsService.testSFConnection(data)
      .then((response: SalesforceConnection) => {
        if (response?.accessToken) {
          handleTestSuccess();
        } else {
          handleTestFailure();
        }
      })
      .catch((error) => {
        handleTestFailure(error);
      })
      .finally(() => {
        setTestIsConnecting(false);
      });
  };

  const handleUpdate = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(true);

    const data = cloneDeep(sfConnectionData);

    data.performValidation = !isSaveOnlyMode;
    data.isSandbox =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SANDBOX;
    data.isScratchOrg =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SCRATCH_ORG;
    data.applicationId = isGlobalConnection
      ? selectedApplication?.id
      : projectPreference?.selectedApplicationId;

    handleUpdateSelectedConnection(data);

    SalesforceConnectionsService.update(data)
      .then(handleSuccess)
      .catch((error: string) => {
        handleFailure(data, error);
      })
      .finally(() => {
        setIsConnecting(false);
      });
  };

  const { handleOAuthLogin, isConnecting: isConnectingOAuth } = useOAuthFlow({
    onAuthComplete: !isTestConnection ? handleUpdate : handleTestConnection,
    onAuthError: handleFailure,
  });

  const handleUsernamePasswordLogin = (
    sfConnectionData: SalesforceConnection,
  ) => {
    isTestConnection
      ? handleTestConnection(sfConnectionData)
      : handleUpdate(sfConnectionData);
  };

  return {
    isConnecting: isConnectingOAuth || isConnecting,
    hasConnectionFailed: selectedConnection?.hasFailed,
    handleUsernamePasswordLogin,
    handleUpdate,
    handleOAuthLogin,
    handleClose,
    testIsConnecting,
  };
};
